import React from 'react';
import { ExpandableSection, Table, StatusIndicator, Badge, Box } from '@amzn/awsui-components-react/polaris';
import ServiceCollection from 'src/services/ServiceCollection';
import {
    PassthroughCostDetailData,
    PassthroughComponentData,
    PLIReconciliationData,
    PLIReconciliationCheckData,
    MultiplierDatasourceRef,
    SegmentCustomCOARef,
} from 'src/models/reports/TPTaxReport';
import {
    SECTION_HEADERS,
    PASSTHROUGH_COLUMNS,
    PLI_COLUMNS,
    RECONCILIATION_STATUS,
    PASSTHROUGH_TOOLTIPS,
} from './PassthroughCostConstants';
import { SectionWithCurrency } from './TPTaxReportView';
import CONSTANTS from 'src/utils/constants';
import './PassthroughCostSections.scss';

/** Formats a numeric currency value using the service formatter, displaying zero as "0". */
const formatCurrency = (value: number, serviceCollection: ServiceCollection): string => {
    if (value === 0) return '0';
    return serviceCollection.formattingService.formatString(String(value), false);
};

/**
 * Renders the FRAMES currency at the top of a per-CLI expandable's content in the multi-CLI
 * (LEF/CWB) Passthrough Cost and PLI Reconciliation sections. Renders nothing when the CLI has
 * no currency, so those CLIs simply omit the line.
 */
const CliCurrencyLine = ({ currency }: { currency?: string }): JSX.Element | null => {
    if (!currency) return null;
    return (
        <Box padding={{ bottom: 'xs' }} data-testid="cli-frames-currency">
            <b>Currency:</b> {currency}
        </Box>
    );
};

/** Builds a navigation href for a datasource reference based on its type. */
function getDatasourceHref(ref: MultiplierDatasourceRef): string | null {
    const encoded = btoa(ref.datasourceReferenceId);
    switch (ref.datasourceType) {
        case 'CUSTOM_TABLE':
            return `${CONSTANTS.PAGE_NAV.CUSTOM_DATA_TABLE_DETAILS.URL}/${encoded}`;
        case 'TP_ALLOCATION_WORKSHEET':
            return `${CONSTANTS.PAGE_NAV.TP_ALLOCATION_STANDALONE.URL}/${encoded}`;
        default:
            return null;
    }
}

/**
 * Renders a multiplierDerivation string with datasource names converted to hyperlinkgit add s.
 * Each datasource name found in the refs list is replaced with a link to its detail page.
 * Names are sorted longest-first to avoid partial replacement collisions.
 */
function renderDerivationWithLinks(derivation: string, refs: MultiplierDatasourceRef[]): React.ReactNode {
    if (!refs || refs.length === 0) {
        return derivation;
    }

    // Build a map of unique datasource names to their href (first match wins for duplicate names)
    const nameToHref = new Map<string, string | null>();
    for (const ref of refs) {
        if (!nameToHref.has(ref.datasourceName)) {
            nameToHref.set(ref.datasourceName, getDatasourceHref(ref));
        }
    }

    // Sort names longest-first so longer names are matched before shorter substrings
    const sortedNames = Array.from(nameToHref.keys()).sort((a, b) => b.length - a.length);

    // Build a regex that matches any datasource name (escaped for regex special chars)
    const escaped = sortedNames.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'g');

    // Split the derivation by matched names, interleaving text and links
    const parts = derivation.split(pattern);
    return parts.map((part, index) => {
        const href = nameToHref.get(part);
        if (href) {
            return (
                <a key={index} href={href} className="passthrough-cost-detail__derivation-link">
                    {part}
                </a>
            );
        }
        return <span key={index}>{part}</span>;
    });
}

/**
 * Maps backend COAType enum values to the display labels used in the segment filter string.
 * E.g. "COST_CENTER" → "Cost Center", "LOCATION" → "Location".
 */
const COA_TYPE_TO_DISPLAY_LABEL: Record<string, string> = {
    COST_CENTER: CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI,
    LOCATION: CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI,
    PRODUCT_LINE: CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI,
    CHANNEL: CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI,
    PROJECT: CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI,
    LG_CODE: CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI,
};

/**
 * Renders a segment filter string with Custom COA display values hyperlinked to their table detail page.
 * The filter string format is "Label1=Value1; Label2=Value2". Each segment is split by "; " and then
 * by "=" to isolate the label and value. If a ref's segmentType maps to the segment's label, the value
 * portion is rendered as a hyperlink to the Custom COA table detail page.
 */
function renderSegmentFilterWithLinks(
    segmentFilter: string,
    refs: SegmentCustomCOARef[] | null | undefined
): React.ReactNode {
    if (!refs || refs.length === 0) {
        return segmentFilter;
    }

    // Build a map of display label → href by converting segmentType enum to UI label
    const labelToHref = new Map<string, string>();
    for (const ref of refs) {
        if (ref.segmentType && ref.customCOATableId) {
            const displayLabel = COA_TYPE_TO_DISPLAY_LABEL[ref.segmentType];
            if (displayLabel) {
                const encoded = btoa(ref.customCOATableId);
                labelToHref.set(displayLabel, `${CONSTANTS.PAGE_NAV.CUSTOM_COA_DETAILS.URL}/${encoded}`);
            }
        }
    }

    // Split by "; " to get individual segments like "Cost Center=1000"
    const segments = segmentFilter.split('; ');

    return (
        <>
            {segments.map((segment, index) => {
                const eqIndex = segment.indexOf('=');
                const separator = index > 0 ? '; ' : '';

                if (eqIndex === -1) {
                    // No "=" found — render as plain text
                    return <span key={index}>{separator}{segment}</span>;
                }

                const label = segment.substring(0, eqIndex);
                const value = segment.substring(eqIndex + 1);
                const href = labelToHref.get(label);

                return (
                    <span key={index}>
                        {separator}{label}=
                        {href ? (
                            <a href={href} className="passthrough-cost-detail__coa-link"
                                title={PASSTHROUGH_TOOLTIPS.CUSTOM_COA_REF_LINK}>
                                {value}
                            </a>
                        ) : (
                            value
                        )}
                    </span>
                );
            })}
        </>
    );
}

interface PassthroughCostDetailProps {
    passthroughCostDetail: PassthroughCostDetailData | null | undefined;
    serviceCollection: ServiceCollection;
    hideContainer?: boolean;
}

interface PLIReconciliationProps {
    pliReconciliation: PLIReconciliationData | null | undefined;
    serviceCollection: ServiceCollection;
    hideContainer?: boolean;
}

interface MultiCLIPassthroughCostProps {
    cliPassthroughDetails: { [cli: string]: PassthroughCostDetailData } | null | undefined;
    serviceCollection: ServiceCollection;
    // Optional CLI -> FRAMES currency map. When a CLI has a currency, it is shown at the top
    // of that CLI's expandable content. CLIs without a currency show nothing.
    cliCurrencyMap?: { [cli: string]: string };
}

interface MultiCLIPLIReconciliationProps {
    cliPliReconciliation: { [cli: string]: PLIReconciliationData } | null | undefined;
    serviceCollection: ServiceCollection;
    // Optional CLI -> FRAMES currency map. When a CLI has a currency, it is shown at the top
    // of that CLI's expandable content. CLIs without a currency show nothing.
    cliCurrencyMap?: { [cli: string]: string };
}

/**
 * Renders passthrough cost detail table for a single CLI calculation.
 * When used standalone (single-CLI report), renders with a container wrapper.
 * When nested inside MultiCLIPassthroughCostSection, set hideContainer=true to avoid double-wrapping.
 */
export function PassthroughCostDetailSection({
    passthroughCostDetail,
    serviceCollection,
    hideContainer,
}: PassthroughCostDetailProps) {
    if (!passthroughCostDetail) return null;

    if (!passthroughCostDetail.derivationSuccessful) {
        const errorContent = <StatusIndicator type="warning">{passthroughCostDetail.errorMessage}</StatusIndicator>;
        if (hideContainer) return errorContent;
        return (
            <SectionWithCurrency>
                <ExpandableSection variant="container" headerText={SECTION_HEADERS.PASSTHROUGH_COST_DETAIL}>
                    {errorContent}
                </ExpandableSection>
            </SectionWithCurrency>
        );
    }

    if (!passthroughCostDetail.components || passthroughCostDetail.components.length === 0) return null;

    const fmt = (value: number) => formatCurrency(value, serviceCollection);

    const tableContent = (
        <Table
            items={passthroughCostDetail.components}
            columnDefinitions={[
                {
                    id: PASSTHROUGH_COLUMNS.DATA_SOURCE_NAME.id,
                    header: PASSTHROUGH_COLUMNS.DATA_SOURCE_NAME.header,
                    cell: (item: PassthroughComponentData) => (
                        <span
                            className="passthrough-cost-detail__ds-badge"
                            title={PASSTHROUGH_TOOLTIPS.DATA_SOURCE_BADGE}>
                            [{item.dataSourceName}]
                        </span>
                    ),
                },
                {
                    id: PASSTHROUGH_COLUMNS.COMPANY.id,
                    header: PASSTHROUGH_COLUMNS.COMPANY.header,
                    cell: (item: PassthroughComponentData) => item.company,
                },
                {
                    id: PASSTHROUGH_COLUMNS.GL_ACCOUNT_AND_SEGMENT.id,
                    header: PASSTHROUGH_COLUMNS.GL_ACCOUNT_AND_SEGMENT.header,
                    cell: (item: PassthroughComponentData) => (
                        <span>
                            {item.glAccount}
                            {item.segmentFilter && (
                                <>
                                    ;{' '}
                                    {item.segmentFilterLink ? (
                                        <a
                                            href={item.segmentFilterLink}
                                            className="passthrough-cost-detail__coa-link"
                                            title={PASSTHROUGH_TOOLTIPS.SEGMENT_FILTER_LINK}>
                                            [{item.segmentFilter}]
                                        </a>
                                    ) : item.segmentCustomCOARefs && item.segmentCustomCOARefs.length > 0 ? (
                                        renderSegmentFilterWithLinks(item.segmentFilter, item.segmentCustomCOARefs)
                                    ) : (
                                        item.segmentFilter
                                    )}
                                </>
                            )}
                        </span>
                    ),
                },
                {
                    id: PASSTHROUGH_COLUMNS.ORIGINAL_GL_BALANCE.id,
                    header: PASSTHROUGH_COLUMNS.ORIGINAL_GL_BALANCE.header,
                    cell: (item: PassthroughComponentData) => (
                        <div className="passthrough-cost-detail__value-right">{fmt(item.originalGLBalance)}</div>
                    ),
                },
                {
                    id: PASSTHROUGH_COLUMNS.MULTIPLIER.id,
                    header: PASSTHROUGH_COLUMNS.MULTIPLIER.header,
                    width: 800,
                    cell: (item: PassthroughComponentData) => {
                        if (item.multiplierValue === 1.0) return '';
                        return (
                            <div className="passthrough-cost-detail__multiplier-cell">
                                <div>{item.multiplierLabel}</div>
                                {item.multiplierDerivation && (
                                    <div className="passthrough-cost-detail__derivation">
                                        {renderDerivationWithLinks(
                                            item.multiplierDerivation,
                                            item.multiplierDatasourceRefs || []
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    },
                },
                {
                    id: PASSTHROUGH_COLUMNS.PASSTHROUGH_CONTRIBUTION.id,
                    header: PASSTHROUGH_COLUMNS.PASSTHROUGH_CONTRIBUTION.header,
                    cell: (item: PassthroughComponentData) => (
                        <div className="passthrough-cost-detail__value-right">{fmt(item.contribution)}</div>
                    ),
                },
            ]}
            footer={
                <div className="passthrough-cost-detail__total-row">
                    <span>{SECTION_HEADERS.TOTAL_PASSTHROUGH_COST}</span>
                    <span>{fmt(passthroughCostDetail.totalPassthroughCost)}</span>
                </div>
            }
        />
    );

    if (hideContainer) return tableContent;

    return (
        <SectionWithCurrency>
            <ExpandableSection variant="container" headerText={SECTION_HEADERS.PASSTHROUGH_COST_DETAIL}>
                {tableContent}
            </ExpandableSection>
        </SectionWithCurrency>
    );
}

/**
 * Renders PLI reconciliation checks table for a single CLI calculation.
 * When used standalone (single-CLI report), renders with a container wrapper.
 * When nested inside MultiCLIPLIReconciliationSection, set hideContainer=true to avoid double-wrapping.
 */
export function PLIReconciliationSection({
    pliReconciliation,
    serviceCollection,
    hideContainer,
}: PLIReconciliationProps) {
    if (!pliReconciliation) return null;
    if (!pliReconciliation.checks || pliReconciliation.checks.length === 0) return null;

    const fmt = (value: number) => formatCurrency(value, serviceCollection);

    const statusBadge = pliReconciliation.allPassed ? (
        <StatusIndicator type="success">All Checks Passed</StatusIndicator>
    ) : (
        <StatusIndicator type="error">{pliReconciliation.failedCount} Checks Failed</StatusIndicator>
    );

    const tableContent = (
        <Table
            items={pliReconciliation.checks}
            columnDefinitions={[
                {
                    id: PLI_COLUMNS.CHECK_NAME.id,
                    header: PLI_COLUMNS.CHECK_NAME.header,
                    cell: (item: PLIReconciliationCheckData) => item.checkName,
                },
                {
                    id: PLI_COLUMNS.PNL_STATEMENT_AMOUNT.id,
                    header: PLI_COLUMNS.PNL_STATEMENT_AMOUNT.header,
                    cell: (item: PLIReconciliationCheckData) => (
                        <div className="passthrough-cost-detail__value-right">{fmt(item.pnlAmount)}</div>
                    ),
                },
                {
                    id: PLI_COLUMNS.PLI_CALCULATION_AMOUNT.id,
                    header: PLI_COLUMNS.PLI_CALCULATION_AMOUNT.header,
                    cell: (item: PLIReconciliationCheckData) => (
                        <div className="passthrough-cost-detail__value-right">{fmt(item.pliAmount)}</div>
                    ),
                },
                {
                    id: PLI_COLUMNS.DELTA.id,
                    header: PLI_COLUMNS.DELTA.header,
                    cell: (item: PLIReconciliationCheckData) => (
                        <div
                            className={
                                item.status !== RECONCILIATION_STATUS.PASS
                                    ? 'pli-reconciliation__delta-fail'
                                    : 'passthrough-cost-detail__value-right'
                            }>
                            {fmt(item.delta)}
                        </div>
                    ),
                },
                {
                    id: PLI_COLUMNS.STATUS.id,
                    header: PLI_COLUMNS.STATUS.header,
                    cell: (item: PLIReconciliationCheckData) => (
                        <Badge color={item.status === RECONCILIATION_STATUS.PASS ? 'green' : 'red'}>
                            {item.status}
                        </Badge>
                    ),
                },
            ]}
        />
    );

    if (hideContainer)
        return (
            <>
                <Box textAlign="right" padding={{ bottom: 's' }}>
                    {statusBadge}
                </Box>
                {tableContent}
            </>
        );

    return (
        <SectionWithCurrency>
            <ExpandableSection
                variant="container"
                headerText={SECTION_HEADERS.PLI_RECONCILIATION}
                headerActions={statusBadge}>
                {tableContent}
            </ExpandableSection>
        </SectionWithCurrency>
    );
}

/**
 * Renders passthrough cost details grouped by CLI for multi-CLI reports (CWB/LEF).
 * Each CLI is an expandable sub-section that shows the passthrough table directly on expand.
 */
export function MultiCLIPassthroughCostSection({
    cliPassthroughDetails,
    serviceCollection,
    cliCurrencyMap,
}: MultiCLIPassthroughCostProps) {
    if (!cliPassthroughDetails || Object.keys(cliPassthroughDetails).length === 0) return null;

    return (
        <ExpandableSection variant="container" headerText={SECTION_HEADERS.PASSTHROUGH_COST_DETAIL}>
            {Object.entries(cliPassthroughDetails).map(([cli, detail]) => (
                <ExpandableSection key={cli} headerText={cli} variant="footer">
                    <CliCurrencyLine currency={cliCurrencyMap?.[cli]} />
                    <PassthroughCostDetailSection
                        passthroughCostDetail={detail}
                        serviceCollection={serviceCollection}
                        hideContainer
                    />
                </ExpandableSection>
            ))}
        </ExpandableSection>
    );
}

/**
 * Renders PLI reconciliation checks grouped by CLI for multi-CLI reports (CWB/LEF).
 * Each CLI is an expandable sub-section that shows the reconciliation table directly on expand.
 */
export function MultiCLIPLIReconciliationSection({
    cliPliReconciliation,
    serviceCollection,
    cliCurrencyMap,
}: MultiCLIPLIReconciliationProps) {
    if (!cliPliReconciliation || Object.keys(cliPliReconciliation).length === 0) return null;

    return (
        <ExpandableSection variant="container" headerText={SECTION_HEADERS.PLI_RECONCILIATION}>
            {Object.entries(cliPliReconciliation).map(([cli, reconciliation]) => (
                <ExpandableSection key={cli} headerText={cli} variant="footer">
                    <CliCurrencyLine currency={cliCurrencyMap?.[cli]} />
                    <PLIReconciliationSection
                        pliReconciliation={reconciliation}
                        serviceCollection={serviceCollection}
                        hideContainer
                    />
                </ExpandableSection>
            ))}
        </ExpandableSection>
    );
}
